import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getCache } from './registry';
import { isCacheEnabled } from '../settings';
import type { RegistryOptions } from './registry';

/**
 * Request-level options for cache wrapper methods.
 */
export interface RequestOptions {
  /** TTL for this request's cached data (ms). */
  ttlMs?: number;
  /** Human string TTL like '1h', '5m', '30s', '200ms'. */
  ttl?: string;
  /** Optional explicit cache key override. */
  key?: string;
}

/**
 * Parse human-readable durations to milliseconds.
 * Accepts: "<n>ms", "<n>s", "<n>m", "<n>h", "<n>d". Defaults to ms when unit omitted.
 */
function parseTtlString(ttl?: string): number | undefined {
  if (!ttl) return undefined;
  const s = ttl.trim().toLowerCase();
  const m = s.match(/^([0-9]+)\s*(ms|s|m|h|d)?$/);
  if (!m) return undefined;
  const n = parseInt(m[1], 10);
  const unit = m[2] || 'ms';
  switch (unit) {
    case 'ms': return n;
    case 's': return n * 1000;
    case 'm': return n * 60_000;
    case 'h': return n * 3_600_000;
    case 'd': return n * 86_400_000;
    default: return undefined;
  }
}

/**
 * Wrap an AxiosInstance with transparent caching (GET/POST/PUT) using the cache registry.
 *
 * Contract
 * - Returns get/post/put methods matching Axios signatures with an optional RequestOptions.
 * - Keys are built from URL, baseURL, params, and for POST/PUT also the body; or overridden via options.key.
 * - TTL can be provided per-call via options.ttlMs; defaults inherited from cache configuration.
 * - Also exposes clear(key?) to clear this scope (or a single key) conveniently.
 *
 * @param axiosInstance The Axios instance to wrap
 * @param scope Cache scope and behaviors (namespace, group, TTL, hashing, meta, etc.)
 *
 * Example:
 * const cached = wrapAxiosInstance(api, { namespace: 'orders', group: 'list', defaultTtl: '15m' });
 * await cached.get('/GetAllOrders', { params: { page: 1 } }, { ttl: '2m' });
 */
export function wrapAxiosInstance(
  axiosInstance: AxiosInstance,
  scope: RegistryOptions = {}
) {
  const cache = getCache(scope);

  return {
    /** Cached GET */
    async get<T = any>(
      url: string,
      config?: AxiosRequestConfig,
      options?: RequestOptions
    ): Promise<AxiosResponse<T>> {
      // Bypass cache globally if disabled
      if (!(await isCacheEnabled())) {
        return axiosInstance.get<T>(url, config);
      }
      const ttl = options?.ttlMs ?? parseTtlString(options?.ttl);
      return cache.wrapAxiosGet<T>(
        axiosInstance,
        url,
        config,
        ttl,
        options?.key
      );
    },

    /** Cached POST (idempotent/safe only) */
    async post<T = any>(
      url: string,
      data?: any,
      config?: AxiosRequestConfig,
      options?: RequestOptions
    ): Promise<AxiosResponse<T>> {
      // Bypass cache globally if disabled
      if (!(await isCacheEnabled())) {
        return axiosInstance.post<T>(url, data, config);
      }
      const ttl = options?.ttlMs ?? parseTtlString(options?.ttl);
      return cache.wrapAxiosPost<T>(
        axiosInstance,
        url,
        data,
        config,
        ttl,
        options?.key
      );
    },

    /** Cached PUT (idempotent/safe only) */
    async put<T = any>(
      url: string,
      data?: any,
      config?: AxiosRequestConfig,
      options?: RequestOptions
    ): Promise<AxiosResponse<T>> {
      // Bypass cache globally if disabled
      if (!(await isCacheEnabled())) {
        return axiosInstance.put<T>(url, data, config);
      }
      const ttl = options?.ttlMs ?? parseTtlString(options?.ttl);
      return cache.wrapAxiosPut<T>(
        axiosInstance,
        url,
        data,
        config,
        ttl,
        options?.key
      );
    },

    /** Clear a single key (relative) or the whole scope if no key provided */
    async clear(key?: string): Promise<void> {
      await cache.clear(key);
    },
  };
}

export default wrapAxiosInstance;
